import { forwardRef } from 'react';
import { formatCurrency, formatMonth, getPaymentTypeLabel } from '../lib/utils';

const Receipt = forwardRef(({ receipt }, ref) => {
  if (!receipt) return null;

  const { payment, tenant, kost, bank_accounts = [] } = receipt;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div ref={ref} className="bg-white p-8 max-w-2xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="text-center border-b-2 border-gray-800 pb-4 mb-6">
        <h1 className="text-2xl font-bold uppercase tracking-wider">{kost.name}</h1>
        <p className="text-sm text-gray-600 mt-1">{kost.address}</p>
        <p className="text-sm text-gray-600">Telp: {kost.phone}</p>
      </div>

      {/* Title */}
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold border-2 border-gray-800 inline-block px-6 py-2">
          KWITANSI PEMBAYARAN
        </h2>
        <p className="text-sm text-gray-600 mt-2">No: {receipt.receipt_number}</p>
      </div>

      {/* Receipt Info */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span>Tanggal</span>
          <span className="font-medium">{formatDate(receipt.date)}</span>
        </div>
      </div>

      {/* Tenant Info */}
      <div className="bg-gray-50 p-4 rounded mb-6">
        <h3 className="font-bold text-sm uppercase mb-3 text-gray-700">Data Penghuni</h3>
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="py-1 w-32">Nama</td>
              <td className="py-1 px-2">:</td>
              <td className="py-1 font-medium">{tenant.name}</td>
            </tr>
            <tr>
              <td className="py-1">No. KTP</td>
              <td className="py-1 px-2">:</td>
              <td className="py-1">{tenant.id_number}</td>
            </tr>
            <tr>
              <td className="py-1">Kamar</td>
              <td className="py-1 px-2">:</td>
              <td className="py-1">{tenant.room_name}</td>
            </tr>
            <tr>
              <td className="py-1">No. HP</td>
              <td className="py-1 px-2">:</td>
              <td className="py-1">{tenant.phone}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment Details */}
      <div className="mb-6">
        <h3 className="font-bold text-sm uppercase mb-3 text-gray-700">Detail Pembayaran</h3>
        <table className="w-full text-sm border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-3 text-left border-b">Keterangan</th>
              <th className="py-2 px-3 text-right border-b">Jumlah</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-3 px-3 border-b">
                <div className="font-medium">{getPaymentTypeLabel(payment.payment_type)}</div>
                <div className="text-xs text-gray-500">Periode: {formatMonth(payment.month)}</div>
                {payment.description && (
                  <div className="text-xs text-gray-500 mt-1">{payment.description}</div>
                )}
              </td>
              <td className="py-3 px-3 text-right border-b font-medium">
                {formatCurrency(payment.amount)}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="bg-gray-50">
              <td className="py-3 px-3 font-bold">TOTAL</td>
              <td className="py-3 px-3 text-right font-bold text-lg">
                {formatCurrency(payment.amount)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Bank Account Info */}
      {bank_accounts.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 rounded border border-blue-200">
          <h3 className="font-bold text-sm uppercase mb-3 text-blue-800">Pembayaran Via Transfer</h3>
          <div className="space-y-3">
            {bank_accounts.map((account, index) => (
              <div key={account.id || index} className="text-sm">
                <div className="font-medium text-blue-900">{account.bank_name}</div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">No. Rek:</span>
                  <span className="font-mono font-bold">{account.account_number}</span>
                </div>
                <div className="text-gray-600">a.n. {account.account_holder}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Status */}
      <div className="flex justify-between items-center mb-8 p-3 bg-green-50 rounded border border-green-200">
        <span className="text-sm">Status Pembayaran:</span>
        <span className={`font-bold uppercase ${payment.status === 'lunas' ? 'text-green-600' : 'text-orange-600'}`}>
          {payment.status === 'lunas' ? 'LUNAS' : 'BELUM LUNAS'}
        </span>
      </div>

      {/* Signatures */}
      <div className="flex justify-between mt-12">
        <div className="text-center">
          <p className="text-sm mb-16">Penghuni,</p>
          <p className="font-medium border-t border-gray-400 pt-1 px-4">{tenant.name}</p>
        </div>
        <div className="text-center">
          <p className="text-sm mb-16">Pemilik Kost,</p>
          <p className="font-medium border-t border-gray-400 pt-1 px-4">________________</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-dashed text-center text-xs text-gray-500">
        <p>Kwitansi ini merupakan bukti pembayaran yang sah.</p>
        <p>Dicetak pada: {new Date().toLocaleString('id-ID')}</p>
      </div>
    </div>
  );
});

Receipt.displayName = 'Receipt';

export default Receipt;
